import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import {
    InteractionType,
    IPublicClientApplication,
    PublicClientApplication,
    BrowserCacheLocation,
} from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import { PortalConfigProvider } from '@shared/portal-config-provider';
import { ThemingService } from '@shared/services/theming.service';
import { environment } from '../environments/environment';

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: environment.msalConfig.auth.authority,
            redirectUri: globalThis.location === undefined ? '/' : `${globalThis.location.origin}/`,
            postLogoutRedirectUri: globalThis.location === undefined ? '/login' : `${globalThis.location.origin}/login`,
            navigateToLoginRequestUrl: true
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false,
            secureCookies: true,
            claimsBasedCachingEnabled: true
        },
        system: {
            windowHashTimeout: 60000,
            iframeHashTimeout: 6000,
            loadFrameTimeout: 0,
            asyncPopups: false
        }
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
    // Absolute API base
    protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
    // Relative API base (used by HttpClient calls like '/api/me')
    protectedResourceMap.set('/api', environment.apiConfig.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: protectedResourceMap
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: environment.msalConfig.auth.scopes || []
        },
        loginFailedRoute: '/login'
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        PortalConfigProvider,
        provideAppInitializer(() => {
            const themingService = inject(ThemingService);
            return themingService.apply();
        }),
        provideAngularSvgIcon()
    ]
};
